import * as React from "react";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
import WeddingPartyModal from "../WeddingPartyModal";
import { GetWeddingPartyQuery } from "@src/@types/api.d";
import { motion } from "framer-motion";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";
import { WeddingPartyType } from "@src/components/CustomTemplate2/WeddingParty";

export type IWeddingParty = {
  eventId: number;
  weddingPartyData: GetWeddingPartyQuery;
  loading?: boolean;
  sectionImage?: string;
};

const WeddingParty: React.FC<IWeddingParty> = ({
  eventId,
  weddingPartyData,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Set initial value
    setIsMobile(typeof window !== "undefined" && window.innerWidth < 768);

    // Handle resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const weddingPartyMembers = (weddingPartyData?.getWeddingParty || []).filter(
    (member): member is WeddingPartyType =>
      member !== null &&
      !!member.first_name &&
      !!member.last_name &&
      !!member.role
  );

  const sortedMembers = [...weddingPartyMembers].sort((a, b) => {
    if (a?.order_index !== null && b?.order_index !== null) {
      return a?.order_index! - b?.order_index!;
    }
    if (a?.order_index !== null && b?.order_index === null) return -1;
    if (a?.order_index === null && b?.order_index !== null) return 1;
    return 0;
  });

  const [currentPage, setCurrentPage] = useState(0);
  const CARDS_PER_PAGE = 3;
  const totalPages = Math.ceil(sortedMembers.length / CARDS_PER_PAGE);

  const scrollToSection = () => {
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      scrollToSection();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      scrollToSection();
    }
  };

  if (!sortedMembers.length) return null;

  const startIdx = currentPage * CARDS_PER_PAGE;
  const visibleMembers = sortedMembers.slice(
    startIdx,
    startIdx + CARDS_PER_PAGE
  );
  const desktopGridColsClass =
    visibleMembers.length === 1
      ? "grid-cols-1"
      : visibleMembers.length === 2
      ? "grid-cols-2"
      : "grid-cols-3";

  const renderCard = (member: WeddingPartyType) => {
    const cardContent = (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col w-full md:w-80 lg:w-[360px]"
      >
        <div
          className={`flex flex-col bg-white border border-[#b18958]/20 rounded-2xl overflow-hidden transition-all duration-300 relative
         h-[450px] md:h-[500px] shadow-lg hover:shadow-xl hover:-translate-y-1`}
        >
          <div className="relative w-full h-80 overflow-hidden bg-transparent">
            <Image
              placeholder="blur"
              blurDataURL={`data:image/svg+xml;base64,${toBase64(
                shimmer(300, 400)
              )}`}
              layout="fill"
              objectFit="cover"
              objectPosition="top"
              src={member?.image_url || flowerBackground}
              alt={`${member?.first_name} ${member?.last_name}`}
              className="transition-transform duration-300 hover:scale-105"
              onError={(event) => {
                const target = event.currentTarget as HTMLImageElement;
                if (!target.src.includes("bw-flower.jpeg")) {
                  target.src = flowerBackground.src;
                }
              }}
            />
          </div>

          <div className="p-4 md:p-5 flex flex-col justify-start flex-1">
            <h3 className="text-xl md:text-2xl font-semibold text-[#454545] md:mb-1 leading-tight font-bauerBodoni">
              {member?.first_name}{" "}
              {member?.nickname ? `"${member.nickname}"` : ""}{" "}
              {member?.last_name}
            </h3>
            <p
              className={`${
                member?.role.length ?? 0 > 12 ? "text-xs" : "text-sm"
              } md:text-base text-[#b18958] mb-1 md:mb-2 uppercase tracking-widest font-medium font-openSans`}
            >
              {member?.role}
            </p>
            {member?.story && (
              <article
                className="text-sm md:text-base font-light text-[#454545] opacity-80 font-openSans w-full"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: isMobile ? 2 : 3,
                  lineClamp: isMobile ? 2 : 3,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  minHeight: 0,
                  maxHeight: isMobile ? "4em" : "4.5em",
                }}
              >
                {member.story}
              </article>
            )}
          </div>
        </div>
      </motion.div>
    );

    return (
      <Dialog
        key={member?.id}
        trigger={cardContent}
        onOpenChange={setIsModalOpen}
      >
        <WeddingPartyModal
          name={
            member?.nickname
              ? `${member?.first_name} "${member.nickname}" ${member?.last_name}`
              : `${member?.first_name} ${member?.last_name}`
          }
          nickname={undefined}
          role={member?.role ?? ""}
          story={member?.story || ""}
          imageUrl={member?.image_url || ""}
        />
      </Dialog>
    );
  };

  return (
    <div
      ref={sectionRef}
      className="w-full py-6 md:py-12 overflow-hidden relative bg-[#EAE4CC]"
    >
      <div className="text-center mb-4 relative z-10">
        <h3 className="font-bauerBodoni text-2xl md:text-4xl font-light text-[#454545] uppercase tracking-wider">
          Our Wedding Party
        </h3>
      </div>

      {/* Mobile: Single column scrollable */}
      <div className="md:hidden px-4 pb-8">
        <div className="space-y-6">
          {visibleMembers.map((member) => renderCard(member))}
        </div>

        {/* Bottom chevrons for mobile */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <motion.button
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              className="w-[50px] h-[50px] rounded-full border border-[#b18958] bg-[#b18958] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Previous"
            >
              <svg
                width="24"
                height="20"
                viewBox="0 0 56 87"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: "scaleX(-1)" }}
              >
                <g>
                  <line
                    transform="rotate(101.38104248046875 27.75 63.25)"
                    y2="83.75"
                    x2="53.75"
                    y1="42.75"
                    x1="1.75"
                    strokeWidth="5"
                    stroke="#fff"
                    fill="none"
                  />
                  <line
                    y2="41.75"
                    x2="52.75"
                    y1="0.75"
                    x1="0.75"
                    strokeWidth="5"
                    stroke="#fff"
                    fill="none"
                  />
                </g>
              </svg>
            </motion.button>
            <motion.button
              onClick={goToNextPage}
              disabled={currentPage === totalPages - 1}
              className="w-[50px] h-[50px] rounded-full border border-[#b18958] bg-[#b18958] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Next"
            >
              <svg
                width="24"
                height="20"
                viewBox="0 0 56 87"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <line
                    transform="rotate(101.38104248046875 27.75 63.25)"
                    y2="83.75"
                    x2="53.75"
                    y1="42.75"
                    x1="1.75"
                    strokeWidth="5"
                    stroke="#fff"
                    fill="none"
                  />
                  <line
                    y2="41.75"
                    x2="52.75"
                    y1="0.75"
                    x1="0.75"
                    strokeWidth="5"
                    stroke="#fff"
                    fill="none"
                  />
                </g>
              </svg>
            </motion.button>
          </div>
        )}
      </div>

      {/* Desktop: 3-column grid */}
      <div className="hidden md:block max-w-7xl mx-auto md:border border-[#fff] p-2">
        <div className="bg-transparent md:bg-white md:border border-[#b18958]/20 md:shadow-xl p-4 md:p-6">
          <div className="flex items-center justify-between gap-6">
            {/* Left chevron */}
            {totalPages > 1 && (
              <motion.button
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="flex-shrink-0 w-[50px] h-[50px] rounded-full border border-[#b18958] bg-[#b18958] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Previous group"
              >
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 56 87"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ transform: "scaleX(-1)" }}
                >
                  <g>
                    <line
                      transform="rotate(101.38104248046875 27.75 63.25)"
                      y2="83.75"
                      x2="53.75"
                      y1="42.75"
                      x1="1.75"
                      strokeWidth="5"
                      stroke="#fff"
                      fill="none"
                    />
                    <line
                      y2="41.75"
                      x2="52.75"
                      y1="0.75"
                      x1="0.75"
                      strokeWidth="5"
                      stroke="#fff"
                      fill="none"
                    />
                  </g>
                </svg>
              </motion.button>
            )}

            {/* Grid of cards */}
            <div className="flex-1 flex justify-center">
              <div
                className={`grid ${desktopGridColsClass} gap-8 lg:gap-10 w-fit justify-items-center`}
              >
                {visibleMembers.map((member) => renderCard(member))}
              </div>
            </div>

            {/* Right chevron */}
            {totalPages > 1 && (
              <motion.button
                onClick={goToNextPage}
                disabled={currentPage === totalPages - 1}
                className="flex-shrink-0 w-[50px] h-[50px] rounded-full border border-[#b18958] bg-[#b18958] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Next group"
              >
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 56 87"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g>
                    <line
                      transform="rotate(101.38104248046875 27.75 63.25)"
                      y2="83.75"
                      x2="53.75"
                      y1="42.75"
                      x1="1.75"
                      strokeWidth="5"
                      stroke="#fff"
                      fill="none"
                    />
                    <line
                      y2="41.75"
                      x2="52.75"
                      y1="0.75"
                      x1="0.75"
                      strokeWidth="5"
                      stroke="#fff"
                      fill="none"
                    />
                  </g>
                </svg>
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeddingParty;
